import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getCoreRowModel,
  getFacetedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import {
  getLeafColumnIds,
  mergeColumnOrder,
} from '#/hooks/table/mergeColumnOrder';
import {
  readTablePrefs,
  writeTablePrefs,
} from '#/utils/userTablePrefsStorage';

const EMPTY_PAGINATION = {
  current_page: 1,
  page_size: 10,
  total_count: 0,
  total_page: 1,
};

/** Client 模式預設每頁筆數（與 EMPTY_PAGINATION.page_size 對齊） */
const DEFAULT_CLIENT_PAGE_SIZE = 10;

const PREFS_WRITE_DEBOUNCE_MS = 300;

function resolveRowId(row, index) {
  const raw = row?.id ?? row?.uuid ?? row?.code;
  if (raw == null || raw === '') {
    return `row-${index}`;
  }
  return String(raw);
}

function resolveInitialClientPageSize(options, apiPagination) {
  if (options.initialPageSize != null) {
    return options.initialPageSize;
  }

  if (apiPagination?.page_size != null) {
    return apiPagination.page_size;
  }

  return DEFAULT_CLIENT_PAGE_SIZE;
}

function resolveColumnDefId(columnDef) {
  return columnDef?.id ?? columnDef?.accessorKey ?? null;
}

function canPersistTablePrefs(tableKey, userId) {
  return Boolean(tableKey) && Boolean(userId);
}

function readInitialPrefs(options) {
  if (!canPersistTablePrefs(options.tableKey, options.userId)) {
    return null;
  }
  return readTablePrefs(options.userId, options.tableKey);
}

function useTable(data = [], columns = [], apiPagination = EMPTY_PAGINATION, options = {}) {
  const {
    isLoading = false,
    onRefetch,
    onPageChange,
    onPageSizeChange,
    onSortingChange: onSortingChangeExternal,
    onRowSelectionChange: onRowSelectionChangeExternal,
    onColumnOrderChange: onColumnOrderChangeExternal,
    disableGlobalFilter = false,
    enableRowSelection = false,
    enableMultiSelection = false,
    serverSide = false,
    isMultiSortEvent,
    enableColumnOrder = false,
    tableKey = null,
    userId = null,
    initialColumnOrder = null,
  } = options;

  const persistEnabled = canPersistTablePrefs(tableKey, userId);
  const initialPrefsRef = useRef(null);
  if (initialPrefsRef.current === null && persistEnabled) {
    initialPrefsRef.current = readInitialPrefs({ tableKey, userId });
  }

  const leafColumnIds = useMemo(
    () => getLeafColumnIds(columns.map((columnDef) => ({
      id: resolveColumnDefId(columnDef),
      meta: columnDef.meta,
      columnDef,
    }))),
    [columns],
  );

  const leafColumnsForMerge = useMemo(
    () => columns.map((columnDef) => ({
      id: resolveColumnDefId(columnDef),
      meta: columnDef.meta,
      columnDef,
    })),
    [columns],
  );

  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnSizing, setColumnSizing] = useState(
    () => initialPrefsRef.current?.columnSizing ?? {},
  );
  const [columnOrder, setColumnOrder] = useState(() => {
    if (!enableColumnOrder) {
      return [];
    }
    const saved = initialColumnOrder
      ?? initialPrefsRef.current?.columnOrder
      ?? null;
    return mergeColumnOrder(saved, leafColumnsForMerge);
  });
  const [rowSelection, setRowSelection] = useState(() => options.initialRowSelection ?? {});
  const [clientPagination, setClientPagination] = useState(() => ({
    pageIndex: 0,
    pageSize: resolveInitialClientPageSize(options, apiPagination),
  }));

  const prefsWriteTimerRef = useRef(null);
  const latestPrefsRef = useRef({ columnOrder, columnSizing });
  latestPrefsRef.current = { columnOrder, columnSizing };

  const schedulePrefsWrite = useCallback(() => {
    if (!persistEnabled) {
      return;
    }
    if (prefsWriteTimerRef.current) {
      clearTimeout(prefsWriteTimerRef.current);
    }
    prefsWriteTimerRef.current = setTimeout(() => {
      prefsWriteTimerRef.current = null;
      const { columnOrder: order, columnSizing: sizing } = latestPrefsRef.current;
      writeTablePrefs(userId, tableKey, {
        columnOrder: order,
        columnSizing: sizing,
      });
    }, PREFS_WRITE_DEBOUNCE_MS);
  }, [persistEnabled, userId, tableKey]);

  useEffect(() => () => {
    if (prefsWriteTimerRef.current) {
      clearTimeout(prefsWriteTimerRef.current);
    }
  }, []);

  const leafColumnIdsKey = leafColumnIds.join('|');

  // columns 增刪時重算 order（保留可排欄 saved 順序）
  useEffect(() => {
    if (!enableColumnOrder) {
      return;
    }
    setColumnOrder((prev) => mergeColumnOrder(prev, leafColumnsForMerge));
    // leafColumnIdsKey 代表 columns 身分集合；故意不把 leafColumnsForMerge 列入 deps
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see leafColumnIdsKey
  }, [enableColumnOrder, leafColumnIdsKey]);

  const resetClientPageIndex = useCallback(() => {
    setClientPagination((prev) => (
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
    ));
  }, []);

  const handleRowSelectionChangeSingle = useCallback((updater) => {
    setRowSelection((old) => {
      const newSelection = typeof updater === 'function' ? updater(old) : updater;
      const selectedKeys = Object.keys(newSelection).filter((key) => newSelection[key]);

      if (selectedKeys.length > 1) {
        const lastSelectedKey = selectedKeys.find((key) => !old[key])
          || selectedKeys[selectedKeys.length - 1];
        const next = { [lastSelectedKey]: true };
        onRowSelectionChangeExternal?.(next);
        return next;
      }

      onRowSelectionChangeExternal?.(newSelection);
      return newSelection;
    });
  }, [onRowSelectionChangeExternal]);

  const handleRowSelectionChangeMulti = useCallback((updater) => {
    setRowSelection((old) => {
      const next = typeof updater === 'function' ? updater(old) : updater;
      onRowSelectionChangeExternal?.(next);
      return next;
    });
  }, [onRowSelectionChangeExternal]);

  const handleRowSelectionChange = enableMultiSelection
    ? handleRowSelectionChangeMulti
    : handleRowSelectionChangeSingle;

  const handleSortingChange = useCallback((updater) => {
    setSorting((old) => {
      const nextSorting = typeof updater === 'function' ? updater(old) : updater;
      if (onSortingChangeExternal) {
        onSortingChangeExternal(nextSorting);
      }
      return nextSorting;
    });
    if (!serverSide) {
      resetClientPageIndex();
    }
  }, [onSortingChangeExternal, serverSide, resetClientPageIndex]);

  const handleColumnFiltersChange = useCallback((updater) => {
    setColumnFilters(updater);
    if (!serverSide) {
      resetClientPageIndex();
    }
  }, [serverSide, resetClientPageIndex]);

  const handleGlobalFilterChange = useCallback((updater) => {
    if (disableGlobalFilter) {
      return;
    }
    setGlobalFilter(updater);
    if (!serverSide) {
      resetClientPageIndex();
    }
  }, [disableGlobalFilter, serverSide, resetClientPageIndex]);

  const handleColumnOrderChange = useCallback((updater) => {
    if (!enableColumnOrder) {
      return;
    }
    setColumnOrder((old) => {
      const nextRaw = typeof updater === 'function' ? updater(old) : updater;
      const next = mergeColumnOrder(nextRaw, leafColumnsForMerge);
      latestPrefsRef.current = {
        ...latestPrefsRef.current,
        columnOrder: next,
      };
      onColumnOrderChangeExternal?.(next);
      return next;
    });
    schedulePrefsWrite();
  }, [
    enableColumnOrder,
    leafColumnsForMerge,
    onColumnOrderChangeExternal,
    schedulePrefsWrite,
  ]);

  const handleColumnSizingChange = useCallback((updater) => {
    setColumnSizing((old) => {
      const next = typeof updater === 'function' ? updater(old) : updater;
      latestPrefsRef.current = {
        ...latestPrefsRef.current,
        columnSizing: next,
      };
      return next;
    });
    schedulePrefsWrite();
  }, [schedulePrefsWrite]);

  const table = useReactTable({
    data,
    columns,
    defaultColumn: {
      minSize: 48,
      maxSize: 640,
    },
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    state: {
      globalFilter: disableGlobalFilter ? '' : globalFilter,
      sorting,
      columnFilters,
      columnSizing,
      ...(enableColumnOrder && columnOrder.length > 0 ? { columnOrder } : {}),
      ...(!serverSide && { pagination: clientPagination }),
      ...(enableRowSelection && { rowSelection }),
    },
    onGlobalFilterChange: disableGlobalFilter ? () => {} : handleGlobalFilterChange,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onColumnSizingChange: handleColumnSizingChange,
    ...(enableColumnOrder && { onColumnOrderChange: handleColumnOrderChange }),
    ...(!serverSide && {
      onPaginationChange: setClientPagination,
      getPaginationRowModel: getPaginationRowModel(),
    }),
    getRowId: resolveRowId,
    ...(enableRowSelection && {
      onRowSelectionChange: handleRowSelectionChange,
      enableRowSelection: true,
    }),
    getCoreRowModel: getCoreRowModel(),
    ...(!serverSide && {
      getSortedRowModel: getSortedRowModel(),
      getFacetedRowModel: getFacetedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
    }),
    ...(serverSide && {
      getFilteredRowModel: getCoreRowModel(),
    }),
    manualSorting: serverSide,
    manualPagination: serverSide,
    enableSorting: true,
    enableMultiSort: true,
    enableColumnFilters: true,
    enableGlobalFilter: !disableGlobalFilter && !serverSide,
    enableSortingRemoval: true,
    sortDescFirst: false,
    isMultiSortEvent: isMultiSortEvent || ((event) => event.shiftKey),
    meta: {
      enableColumnOrder,
    },
  });

  const filteredItems = table.getFilteredRowModel().rows.length;

  // 資料變少（或 filter 後列數下降）時 clamp，避免停在空頁
  useEffect(() => {
    if (serverSide) {
      return;
    }

    const maxPageIndex = Math.max(0, Math.ceil(filteredItems / clientPagination.pageSize) - 1);
    if (clientPagination.pageIndex <= maxPageIndex) {
      return;
    }

    setClientPagination((prev) => ({
      ...prev,
      pageIndex: maxPageIndex,
    }));
  }, [serverSide, filteredItems, clientPagination.pageIndex, clientPagination.pageSize]);

  const resetFilters = useCallback(() => {
    setGlobalFilter('');
    setColumnFilters([]);
    if (!serverSide) {
      resetClientPageIndex();
    }
  }, [serverSide, resetClientPageIndex]);

  const resetSorting = useCallback(() => {
    setSorting([]);
    if (!serverSide) {
      resetClientPageIndex();
    }
  }, [serverSide, resetClientPageIndex]);

  const handlePageChange = useCallback((newPage) => {
    if (serverSide) {
      onPageChange?.(newPage);
      return;
    }

    setClientPagination((prev) => ({
      ...prev,
      pageIndex: Math.max(0, newPage - 1),
    }));
    onPageChange?.(newPage);
  }, [serverSide, onPageChange]);

  const handlePageSizeChange = useCallback((newPageSize) => {
    if (serverSide) {
      onPageSizeChange?.(newPageSize);
      return;
    }

    setClientPagination({
      pageIndex: 0,
      pageSize: newPageSize,
    });
    onPageSizeChange?.(newPageSize);
  }, [serverSide, onPageSizeChange]);

  const resetAll = useCallback(() => {
    setGlobalFilter('');
    setColumnFilters([]);
    setSorting([]);
    if (!serverSide) {
      resetClientPageIndex();
    }
    if (enableRowSelection) {
      setRowSelection({});
      onRowSelectionChangeExternal?.({});
    }
  }, [serverSide, resetClientPageIndex, enableRowSelection, onRowSelectionChangeExternal]);

  const getSelectedRow = useCallback(() => {
    if (!enableRowSelection) {
      return null;
    }

    const selectedRowIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);
    if (selectedRowIds.length === 0) {
      return null;
    }

    const selectedRowId = selectedRowIds[0];
    return data.find((row, index) => resolveRowId(row, index) === selectedRowId) || null;
  }, [enableRowSelection, rowSelection, data]);

  const getSelectedRows = useCallback(() => {
    if (!enableRowSelection) {
      return [];
    }

    return table.getSelectedRowModel().rows.map((row) => row.original);
  }, [enableRowSelection, table]);

  const clearSelection = useCallback(() => {
    if (enableRowSelection) {
      setRowSelection({});
      onRowSelectionChangeExternal?.({});
    }
  }, [enableRowSelection, onRowSelectionChangeExternal]);

  const resolvedPagination = useMemo(() => {
    if (serverSide) {
      return apiPagination;
    }

    const pageSize = clientPagination.pageSize;
    const totalPage = Math.max(1, Math.ceil(filteredItems / pageSize) || 1);
    const currentPage = Math.min(clientPagination.pageIndex + 1, totalPage);

    return {
      current_page: currentPage,
      page_size: pageSize,
      total_count: filteredItems,
      total_page: totalPage,
    };
  }, [serverSide, apiPagination, clientPagination, filteredItems]);

  return useMemo(() => ({
    table,
    globalFilter,
    setGlobalFilter: handleGlobalFilterChange,
    columnFilters,
    setColumnFilters: handleColumnFiltersChange,
    columnSizing,
    columnOrder,
    setColumnOrder: handleColumnOrderChange,
    enableColumnOrder,
    sorting,
    setSorting,
    pagination: resolvedPagination,
    currentPage: resolvedPagination.current_page,
    pageSize: resolvedPagination.page_size,
    totalCount: resolvedPagination.total_count,
    totalPages: resolvedPagination.total_page,
    totalItems: resolvedPagination.total_count,
    filteredItems,
    isLoading,
    onRefetch,
    handlePageChange,
    handlePageSizeChange,
    resetFilters,
    resetSorting,
    resetAll,
    ...(enableRowSelection && {
      rowSelection,
      setRowSelection,
      getSelectedRow,
      ...(enableMultiSelection && { getSelectedRows }),
      clearSelection,
    }),
  }), [
    table,
    globalFilter,
    handleGlobalFilterChange,
    columnFilters,
    handleColumnFiltersChange,
    columnSizing,
    columnOrder,
    handleColumnOrderChange,
    enableColumnOrder,
    sorting,
    resolvedPagination,
    filteredItems,
    isLoading,
    onRefetch,
    handlePageChange,
    handlePageSizeChange,
    resetFilters,
    resetSorting,
    resetAll,
    enableRowSelection,
    enableMultiSelection,
    rowSelection,
    getSelectedRow,
    getSelectedRows,
    clearSelection,
  ]);
}

export default useTable;
